import { styled } from '@mui/material';
import GenerateAssignmentId from 'components/Assignments/GenerateAssignmentId';
import AssignmentSettings from 'components/InstructorViewAssignment/AssignmentSettings';
import FormativeFeedback from 'components/InstructorViewAssignment/FormativeFeedback';
import PreviewAndGrading from 'components/InstructorViewAssignment/PreviewAndGrading';
import { useAuth } from 'contexts/auth';
import { useTranslation } from 'react-i18next';
import { Button, Container, Divider, Drawer, Icon, Paper, RubricTable, Stack, Text, Tooltip } from 'shell/components';
import ViewMore from 'shell/components/ViewMore';
import { useViewAccordionRubricTableButton } from 'shell/components/ViewRubricTable';

const Desktop = ({
    assignment,
    isDuplicating,
    isGeneratingAssignmentId,
    isPreviewGenerating,
    isSubmissionsGenerating,
    onClickBack,
    onClickDuplicate,
    onClickUnpublish,
    onClickPublish,
    onClickEdit,
    onGenerateAssignmentId,
    onClickViewContentBank,
    onClickViewGradingSettings,
}) => {
    const { auth } = useAuth();
    const { t } = useTranslation('assignment');
    const hasTitle = Boolean(assignment.title?.trim());
    const title = hasTitle ? assignment.title.trim() : t('untitled-assignment', 'Untitled assignment');
    const { AccordionRubricTableButton, isExpanded } = useViewAccordionRubricTableButton();

    return (
        <Container device={'desktop'}>
            <CustomStack spacing={5}>
                <Stack
                    alignItems={'center'}
                    direction={'row'}
                    flexWrap={'wrap'}
                    justifyContent={'space-between'}
                    sx={{ rowGap: 0 }}
                >
                    <Button
                        sx={{ alignSelf: 'flex-start', minHeight: '2.25rem' }}
                        startIcon={<Icon kind={'arrowLeft'} />}
                        variant={'borderless'}
                        onClick={onClickBack}
                    >
                        {t('common:controls.back', 'Back')}
                    </Button>
                    <Stack direction={'row'} spacing={3} sx={{ flexShrink: 0, marginLeft: 'auto' }}>
                        <Button
                            disabled={assignment.api_assignment_id && auth.role === 'ta'}
                            variant={'tertiary'}
                            onClick={
                                assignment.is_draft
                                    ? () => onClickPublish(assignment)
                                    : () => onClickUnpublish(assignment)
                            }
                        >
                            {assignment.is_draft
                                ? t('common:controls.publish', 'Publish')
                                : t('common:controls.unpublish', 'Unpublish')}
                        </Button>
                        <Button
                            loading={isDuplicating}
                            variant={'tertiary'}
                            onClick={() => onClickDuplicate(assignment)}
                        >
                            {t('common:controls.duplicate', 'Duplicate')}
                        </Button>
                        <Button
                            disabled={
                                isPreviewGenerating ||
                                isSubmissionsGenerating ||
                                (assignment.api_assignment_id && auth.role === 'ta')
                            }
                            startIcon={<Icon kind={'edit'} />}
                            tooltip={
                                isPreviewGenerating || isSubmissionsGenerating
                                    ? {
                                          component: t(
                                              'general-section.edit-disabled-tooltip',
                                              'Please wait until submissions are uploaded before continuing to edit.'
                                          ),
                                      }
                                    : undefined
                            }
                            variant={'tertiary'}
                            onClick={onClickEdit}
                        >
                            {t('common:controls.edit', 'Edit')}
                        </Button>
                    </Stack>
                </Stack>
                <Stack spacing={4}>
                    <Stack spacing={1}>
                        <Text
                            color={hasTitle ? undefined : 'light'}
                            component={'h1'}
                            sx={hasTitle ? undefined : { fontStyle: 'italic' }}
                            variant={'h6'}
                            weight={'semibold'}
                        >
                            {title}
                        </Text>
                        <Text sx={{ fontStyle: 'italic' }} color={'light'}>
                            {assignment.is_draft
                                ? t('common:status.draft', 'Draft')
                                : t('common:status.published', 'Published')}
                        </Text>
                    </Stack>
                    <GenerateAssignmentId
                        generatedId={assignment.generated_id}
                        isGenerating={isGeneratingAssignmentId}
                        showGenerate
                        onGenerate={onGenerateAssignmentId}
                    />
                </Stack>
                <CustomPaper elevation={0}>
                    <Stack spacing={5}>
                        <Stack spacing={2}>
                            <Text>{t('general-section.assignment-description-label', 'Assignment Description')}</Text>
                            {assignment.description ? (
                                <ViewMore TextComponentProps={{ color: 'light', rich: true }}>
                                    {assignment.description}
                                </ViewMore>
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        <Stack spacing={2}>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Text>{t('assignment-rubric-label', 'Assignment Rubric')}</Text>
                                {assignment.rubric && <AccordionRubricTableButton />}
                            </Stack>
                            {assignment.rubric ? (
                                <RubricTable
                                    type={'view'}
                                    assignment={assignment}
                                    isExpanded={isExpanded}
                                    data={assignment.rubric.criteria}
                                />
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        <Stack divider={<Divider />} spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>{t('citation-format.label', 'Citation Format')}</Text>
                                <Tooltip
                                    content={t(
                                        'citation-format.tooltip',
                                        'Grading adapts to your chosen citation style and applies the correct formatting standards (e.g., APA first-page formatting is evaluated differently).'
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            <Text color={'light'}>
                                {t(`citation-format.option.${assignment.citation_format ?? 'none'}`)}
                            </Text>
                        </Stack>
                        <Stack divider={<Divider />} spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>{t('content-bank.title', 'Content Bank')}</Text>
                                <Tooltip
                                    content={t(
                                        'content-bank.secondary-tooltip',
                                        'Files added to the Content Bank allow our AI to retrieve relevant context from these documents, enabling it to generate more personalized and accurate feedback and grading suggestions.'
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            {assignment.content_bank_resources?.filter(({ active }) => active)?.length ? (
                                <Button
                                    sx={{ alignSelf: 'flex-start' }}
                                    variant={'borderless'}
                                    onClick={onClickViewContentBank}
                                >
                                    {t('content-bank.view-content-bank', 'View Content Bank')}
                                </Button>
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        <AssignmentSettings
                            assignment={assignment}
                            onClickViewGradingSettings={onClickViewGradingSettings}
                        />
                    </Stack>
                </CustomPaper>
            </CustomStack>
            <Drawer device={'desktop'} variant={'persistent'} open>
                <Stack divider={<Divider />} spacing={5}>
                    <PreviewAndGrading />
                    <FormativeFeedback />
                </Stack>
            </Drawer>
        </Container>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        alignSelf: 'flex-start',
        width: 'calc(100% - 444px)',
        paddingBottom: theme.spacing(5),
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(5),
    };
});

export default Desktop;
